/*
 * Social Feed Grid — Swiper init for slider mode.
 *
 * Layout is per breakpoint: data-layout-d drives desktop (>=992px) and
 * data-layout-m drives tablet and mobile (<=991px). When either asks for
 * a slider the Liquid markup renders a slider tree wrapping the children
 * in `.swiper > .swiper-wrapper`, with optional arrows + pagination
 * siblings driven by the merchant's per-breakpoint show toggles. CSS
 * shows whichever tree that breakpoint wants.
 * This module marks each direct .swiper-wrapper child as a
 * .swiper-slide and instantiates Swiper with config read from the
 * wrapper's data attrs.
 *
 * Grid + app mode blocks are a no-op — pure CSS / third-party
 * embed handle layout.
 *
 * Re-runs on shopify:section:load so customizer reloads pick up
 * cleanly. shopify:section:unload destroys the swiper and clears
 * the locked-state flag.
 *
 * Swiper is the global UMD vendored at assets/swiper-bundle.min.js
 * (loaded by snippets/scripts.liquid before this file).
 */

const Swiper = window.Swiper;
const SELECTOR = '[data-social-feed]';
const INSTANCE_KEY = '_socialFeedInstance';

class SocialFeedSlider {
  constructor(root) {
    this.root = root;
    this.swiper = null;

    this.init();
  }

  init() {
    // Nothing to wire up when no breakpoint runs a slider — the block is
    // grid-only or an app embed, and there is no slider tree in the DOM.
    const ds0 = this.root.dataset;
    if (ds0.layoutD !== 'slider' && ds0.layoutM !== 'slider') return;

    const swiperEl = this.root.querySelector('.swiper');
    const wrapperEl = swiperEl?.querySelector('.swiper-wrapper');
    if (!swiperEl || !wrapperEl) return;

    // Mark each direct child of .swiper-wrapper as a swiper-slide.
    // The children come from `content_for 'blocks'` — image / video /
    // @app tiles — and need the class for Swiper to lay them out.
    Array.from(wrapperEl.children).forEach((child) => {
      if (!child.classList.contains('swiper-slide')) {
        child.classList.add('swiper-slide');
      }
    });

    const ds = this.root.dataset;
    // slides_per_view_mode gate. When 'auto', Swiper picks slide widths
    // from each slide element's own CSS (the block's stylesheet provides
    // a 280/360px safe-default). When 'custom' (default), the per-
    // breakpoint numbers below drive layout.
    const spvMode = ds.spvMode || 'custom';
    const spvD = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvD) || 5);
    const spvT = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvT) || 4);
    const spvM = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvM) || 2);
    const gapD = parseFloat(ds.gapD) || 8;
    const gapT = parseFloat(ds.gapT) || 8;
    const gapM = parseFloat(ds.gapM) || 8;
    const offsetD = parseFloat(ds.offsetD) || 0;
    const offsetT = parseFloat(ds.offsetT) || 0;
    const offsetM = parseFloat(ds.offsetM) || 0;
    const loop = ds.loop === 'true';
    const autoplay = ds.autoplay === 'true';
    const autoplayDelay = (parseFloat(ds.autoplayDelay) || 5) * 1000;

    // Register navigation / pagination with Swiper if ANY breakpoint
    // shows them. CSS hides them at the breakpoints where the merchant
    // set show_*_{d,t,m} to false.
    const showNavigation =
      ds.showNavigationD === 'true' ||
      ds.showNavigationT === 'true' ||
      ds.showNavigationM === 'true';
    const showPagination =
      ds.showPaginationD === 'true' ||
      ds.showPaginationT === 'true' ||
      ds.showPaginationM === 'true';

    /** @type {any} */
    const options = {
      slidesPerView: spvM,
      spaceBetween: gapM,
      slidesOffsetBefore: offsetM,
      slidesOffsetAfter: offsetM,
      grabCursor: true,
      loop,
      autoplay: autoplay
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        768: {
          slidesPerView: spvT,
          spaceBetween: gapT,
          slidesOffsetBefore: offsetT,
          slidesOffsetAfter: offsetT,
        },
        992: {
          slidesPerView: spvD,
          spaceBetween: gapD,
          slidesOffsetBefore: offsetD,
          slidesOffsetAfter: offsetD,
        },
      },
      // watchOverflow can permanently lock the slider if init catches
      // the viewport at 0px width. Disable + observe parents so the
      // slider stays alive when its container resizes.
      //
      // This matters more now that layout is per breakpoint: with a grid
      // on desktop and a slider on mobile, a desktop page load inits
      // Swiper while its tree is display:none and therefore 0px wide.
      // observer/observeParents recalculate when the media query flips
      // the tree visible, so it must not be locked before that happens.
      watchOverflow: false,
      observer: true,
      observeParents: true,
    };

    if (showNavigation) {
      const prevEl = this.root.querySelector('.slideshow-control--previous');
      const nextEl = this.root.querySelector('.slideshow-control--next');
      if (prevEl && nextEl) {
        options.navigation = { prevEl, nextEl };
      }
    }

    if (showPagination) {
      const paginationEl = this.root.querySelector('.social-feed-grid__pagination');
      if (paginationEl) {
        options.pagination = { el: paginationEl, clickable: true };
      }
    }

    this.swiper = new Swiper(swiperEl, options);

    // Sync the locked-state flag from Swiper. When fewer slides than
    // slidesPerView, Swiper enters "locked" mode and stops responding
    // to swipe / autoplay; CSS hides arrows + pagination via
    // [data-locked="true"] so the UI doesn't advertise navigation it
    // can't perform.
    const setLocked = (locked) => {
      this.root.dataset.locked = locked ? 'true' : 'false';
    };
    setLocked(this.swiper.isLocked);
    this.swiper.on('lock', () => setLocked(true));
    this.swiper.on('unlock', () => setLocked(false));

    this.root.classList.add('is-initialized');
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.root.classList.remove('is-initialized');
    delete this.root.dataset.locked;
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new SocialFeedSlider(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
